import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FlameIcon } from '@shared/ui';
import { isLowResult, isPerfectResult, type FirstTrainingResult, type ReminderChoice } from '../model/flow';
import { ButtonSpinner, OnboardingShell } from './OnboardingShell';

interface OnboardingResultProps {
  /** Итог первой тренировки; null — тренировку не закончили (вышли), цифр нет. */
  result: FirstTrainingResult | null;
  /** «Подтверди почту» — только пока почта не подтверждена (собирает страница). */
  emailSlot?: ReactNode;
  busy: boolean;
  error: string | null;
  onFinish: (choice: ReminderChoice) => void;
}

/**
 * Итог знакомства: «5 из 5!» (верно с первой попытки), 1-й день серии и «На главную». Выбора времени
 * напоминаний нет (решение владельца 2026-10-21): на сервер уходит null — 19:00 МСК по умолчанию;
 * отказаться можно в Настройках и по ссылке в каждом письме.
 */
export function OnboardingResult({ result, emailSlot, busy, error, onFinish }: OnboardingResultProps) {
  const { t } = useTranslation();
  // Пустая тренировка (завершили, не ответив) — ни результата, ни дня серии.
  const scored = !!result && result.answered > 0;

  return (
    <OnboardingShell step={2}>
      {scored && result ? (
        <div className="flex flex-col items-start">
          {isLowResult(result) ? (
            <>
              <h1 className="text-balance font-serif text-[30px] font-medium leading-[1.12] tracking-[-0.02em] sm:text-[36px]">
                {t('onboarding.result.newWords', { count: result.total })}
              </h1>
              <p className="mt-2.5 text-[16px] text-muted">
                {result.correct > 0
                  ? t('onboarding.result.firstTryCount', { count: result.correct })
                  : t('onboarding.result.zeroFirstTry')}
              </p>
            </>
          ) : (
            <>
              <h1 className="font-serif text-[48px] font-medium leading-none tracking-[-0.02em] nav:text-[60px]">
                {t(isPerfectResult(result) ? 'onboarding.result.scorePerfect' : 'onboarding.result.score', {
                  correct: result.correct,
                  total: result.total,
                })}
              </h1>
              <p className="mt-2.5 text-[16px] text-muted">{t('onboarding.result.firstTry')}</p>
            </>
          )}
          <p className="mt-4 inline-flex items-center gap-1.5 rounded-pill border border-accent/25 bg-accent/10 px-3 py-1.5 text-[14px] font-medium text-fg">
            <FlameIcon className="h-4 w-4 text-accent" aria-hidden />
            {t('onboarding.result.streak')}
          </p>
        </div>
      ) : (
        <div>
          <h1 className="text-balance font-serif text-[30px] font-medium leading-[1.12] tracking-[-0.02em] sm:text-[36px]">
            {t('onboarding.result.noScoreTitle')}
          </h1>
          <p className="mt-2.5 text-[16px] text-muted">{t('onboarding.result.noScoreSub')}</p>
        </div>
      )}

      {emailSlot && <div className="mt-7">{emailSlot}</div>}

      <div className="mt-7 flex flex-col gap-3">
        <Button size="lg" fullWidth onClick={() => onFinish(null)} disabled={busy} aria-busy={busy}>
          {busy && <ButtonSpinner />}
          {busy ? t('onboarding.result.saving') : t('onboarding.result.toDashboard')}
        </Button>
        {error && (
          <p role="alert" className="rounded-xl bg-danger/15 px-3.5 py-2.5 text-[14px] leading-snug text-danger">
            {error}
          </p>
        )}
      </div>
    </OnboardingShell>
  );
}
